import { Backsplash, Kitchen } from '@rta/rta-blueprint-library';
import { type ContextMenuList } from '../types/context-menu-list.type';
import { ContextMenuListItemType } from '../types/context-menu-list-item-type.enum';
import { DisplayMode } from '@r-core';

/**
 * Get eligible backsplashes that can be paired with the current one.
 * Searches across ALL countertops in the kitchen to support cross-island merging.
 */
const getEligiblePairings = (backsplash: Backsplash, kitchen: Kitchen): Backsplash[] => {
	if (!kitchen?.design?.countertops?.children) return [];

	const eligibleBacksplashes: Backsplash[] = [];

	// Search across all countertops in the kitchen
	for (const countertop of kitchen.design.countertops.children) {
		if (!countertop?.backsplashes?.children) continue;

		for (const other of countertop.backsplashes.children as Backsplash[]) {
			if (backsplash.canPairWith(other)) {
				eligibleBacksplashes.push(other);
			}
		}
	}

	return eligibleBacksplashes;
};

export const pairBacksplashLeftMenuItem = (): ContextMenuList => {
	return {
		type: ContextMenuListItemType.Item,
		content: 'Merge as Left',
		displayModes: [DisplayMode.Overhead, DisplayMode.OverheadManufacturing],
		isVisible: ({ controller, refMap }): boolean => {
			const { backsplash } = refMap ?? {};
			if (controller.readonly || !backsplash) return false;
			if ((backsplash as Backsplash).isPaired) return false;

			const eligible = getEligiblePairings(backsplash as Backsplash, controller.kitchen);
			return eligible.length > 0;
		},
		onClick: ({ refMap, controller }): void => {
			const backsplash = refMap?.backsplash as Backsplash | null;

			if (backsplash && controller.kitchen) {
				const eligible = getEligiblePairings(backsplash, controller.kitchen);
				if (eligible.length > 0) {
					// Pair with the first eligible backsplash
					backsplash.pairWith(eligible[0], 'left');
				}
			}
		}
	};
};

export const pairBacksplashRightMenuItem = (): ContextMenuList => {
	return {
		type: ContextMenuListItemType.Item,
		content: 'Merge as Right',
		displayModes: [DisplayMode.Overhead, DisplayMode.OverheadManufacturing],
		isVisible: ({ controller, refMap }): boolean => {
			const { backsplash } = refMap ?? {};
			if (controller.readonly || !backsplash) return false;
			if ((backsplash as Backsplash).isPaired) return false;

			const eligible = getEligiblePairings(backsplash as Backsplash, controller.kitchen);
			return eligible.length > 0;
		},
		onClick: ({ refMap, controller }): void => {
			const backsplash = refMap?.backsplash as Backsplash | null;

			if (backsplash && controller.kitchen) {
				const eligible = getEligiblePairings(backsplash, controller.kitchen);
				if (eligible.length > 0) {
					// Pair with the first eligible backsplash
					backsplash.pairWith(eligible[0], 'right');
				}
			}
		}
	};
};

export const unpairBacksplashMenuItem = (): ContextMenuList => {
	return {
		type: ContextMenuListItemType.Item,
		content: 'Unmerge Backsplash',
		displayModes: [DisplayMode.Overhead, DisplayMode.OverheadManufacturing],
		isVisible: ({ controller, refMap }): boolean => {
			const { backsplash } = refMap ?? {};
			if (controller.readonly || !backsplash) return false;

			return (backsplash as Backsplash).isPaired;
		},
		onClick: ({ refMap }): void => {
			const backsplash = refMap?.backsplash as Backsplash | null;

			if (backsplash) {
				backsplash.unpair();
			}
		}
	};
};
